import { FISH_KINDS } from '@if/sim-fish';
/**
 * Сколько стоит пойманная рыба и сколько за неё дают опыта.
 *
 * ⚠️ РЕДКОСТЬ СЧИТАЕТСЯ ОТ ПРЕДЕЛА ВИДА, А НЕ ОТ ПОТОЛКА ГЛУБИНЫ.
 *
 * Соблазн взять `maxWeightForDepth` велик — он же уже посчитан в правилах
 * розыгрыша. Но это ровно та ошибка, которая обесценивает всю экономику.
 * На метровой отмели потолок щуки около 0.8 кг, и восьмисотграммовая щука
 * оказывается там «предельным экземпляром» — то есть по редкости равной
 * девятикилограммовой с ямы. Игрок находит самую мелкую воду, где вид ещё
 * ловится, и доит её трофейными ценами.
 *
 * Поэтому шкала одна на весь вид: девять килограммов у щуки — предел щуки
 * вообще, где бы она ни попалась.
 */
/**
 * Вероятность встретить экземпляр ТЯЖЕЛЕЕ заданного веса, 0..1.
 *
 * Выводится из самого розыгрыша (`rollWeight`), а не подобрана: вес там
 * распределён треугольником по ЛОГАРИФМУ веса, с вершиной на типичном.
 * Для треугольного распределения на [a, b] с вершиной c хвост считается
 * точно, без численного интегрирования:
 *
 *   при x ≥ c:  P(X > x) = (b − x)² / ((b − a)(b − c))
 *   при x < c:  P(X > x) = 1 − (x − a)² / ((b − a)(c − a))
 *
 * где a = ln(minKg), b = ln(maxKg), c = ln(typicalKg).
 *
 * ⚠️ ЭТО НЕ ПРИБЛИЖЕНИЕ И НЕ ПОДГОНКА. Стоит поменять minKg/typicalKg/maxKg
 * в правилах рыбы — цены и опыт поедут следом сами, без второй таблицы,
 * которую забыли бы обновить.
 */
export function rarityChance(spec, kg) {
    const a = Math.log(spec.minKg);
    const b = Math.log(spec.maxKg);
    const c = Math.log(spec.typicalKg);
    const x = Math.log(Math.max(spec.minKg, Math.min(spec.maxKg, kg)));
    const span = b - a;
    if (span <= 1e-9)
        return 1;
    if (x >= c) {
        const up = b - c;
        if (up <= 1e-9)
            return 0;
        return ((b - x) * (b - x)) / (span * up);
    }
    const down = c - a;
    if (down <= 1e-9)
        return 1;
    return 1 - ((x - a) * (x - a)) / (span * down);
}
/**
 * Насколько экземпляр редок, 0…6.2 — это −ln(вероятность).
 *
 * ⚠️ ПОЛ ОБЯЗАТЕЛЕН. Логарифм у самого предела вида уходит в бесконечность,
 * и рыба весом ровно в maxKg стоила бы бесконечно. Пол в две тысячных — это
 * «реже одного раза на пятьсот поимок»; дальше редкость не растёт, и потолок
 * цены становится конечным и известным заранее.
 */
const RARITY_FLOOR = 0.002;
export function rarity(spec, kg) {
    return -Math.log(Math.max(RARITY_FLOOR, rarityChance(spec, kg)));
}
/** Наибольшая возможная редкость — потолок всех формул ниже. */
export const RARITY_MAX = -Math.log(RARITY_FLOOR);
export const FISH_VALUE = {
    pike: { baseCoins: 12, perKgCoins: 34, baseXp: 12 },
    perch: { baseCoins: 6, perKgCoins: 30, baseXp: 6 },
    zander: { baseCoins: 12, perKgCoins: 42, baseXp: 14 },
    asp: { baseCoins: 10, perKgCoins: 26, baseXp: 10 },
};
const DEFAULT_FISH_VALUE = { baseCoins: 10, perKgCoins: 30, baseXp: 10 };
/** Насколько редкость надбавляет к цене. */
const COINS_RARITY_GAIN = 0.35;
/** Насколько редкость надбавляет к опыту. Выше, чем у денег, — см. выше. */
const XP_RARITY_GAIN = 0.9;
/** Сколько монет дадут за рыбу этого вида и веса. */
export function fishCoins(kind, kg) {
    const spec = FISH_KINDS[kind];
    const v = FISH_VALUE[kind] ?? DEFAULT_FISH_VALUE;
    const r = rarity(spec, kg);
    return Math.max(1, Math.round((v.baseCoins + v.perKgCoins * kg) * (1 + COINS_RARITY_GAIN * r)));
}
/** Сколько опыта дадут за поимку рыбы этого вида и веса. */
export function fishXp(kind, kg) {
    const spec = FISH_KINDS[kind];
    const v = FISH_VALUE[kind] ?? DEFAULT_FISH_VALUE;
    const r = rarity(spec, kg);
    return Math.max(1, Math.round(v.baseXp * (1 + XP_RARITY_GAIN * r)));
}
// ── Уровни ───────────────────────────────────────────────────────────────
/** Наивысший уровень. Столько же, сколько обещано в документе прогрессии. */
export const MAX_LEVEL = 60;
/**
 * Сколько опыта надо накопить ВСЕГО, чтобы иметь этот уровень.
 *
 * ⚠️ СЧИТАЕТСЯ, А НЕ ХРАНИТСЯ ТАБЛИЦЕЙ. Таблица на шестьдесят строк — это
 * шестьдесят чисел, которые кто-то однажды подправит на сервере и забудет
 * на клиенте, после чего уровень в меню разойдётся с уровнем в базе.
 *
 * Показатель 1.75 подобран по одному условию: до шестидесятого выходит
 * примерно 82 000 опыта, то есть около четырёх тысяч рыб. При поклёвке раз
 * в пару минут это порядка ста тридцати часов — «дошёл до конца» остаётся
 * достижением, но не работой на годы.
 */
export function xpForLevel(level) {
    const n = Math.max(1, Math.min(MAX_LEVEL, Math.floor(level)));
    return Math.round(80 * (n - 1) ** 1.75);
}
/** Какой уровень отвечает накопленному опыту. */
export function levelForXp(xp) {
    const total = Math.max(0, xp);
    let lo = 1;
    let hi = MAX_LEVEL;
    while (lo < hi) {
        const mid = Math.ceil((lo + hi) / 2);
        if (xpForLevel(mid) <= total)
            lo = mid;
        else
            hi = mid - 1;
    }
    return lo;
}
/** Продвижение внутри уровня: сколько набрано и сколько нужно, 0..1. */
export function levelProgress(xp) {
    const level = levelForXp(xp);
    if (level >= MAX_LEVEL)
        return { level, have: 0, need: 0 };
    const from = xpForLevel(level);
    const to = xpForLevel(level + 1);
    return { level, have: Math.max(0, xp - from), need: Math.max(1, to - from) };
}
//# sourceMappingURL=value.js.map